class SimpleCollections extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.loadedItems = new Set();
    this.init();
  }

  async init() {
    // Small delay to ensure DOM is ready
    await new Promise(resolve => setTimeout(resolve, 100));
    
    this.collectionData = this.getCollectionData();
    this.setupIntersectionObserver();
    this.setupVideoAutoplay();
    this.setupImageLazyLoading();
    
    // Force load any items that should be visible immediately
    this.forceLoadVisibleItems();
  }

  getCollectionData() {
    const script = document.getElementById(`collections-data-${this.sectionId}`);
    if (!script) {
      console.warn('Collection data script not found');
      return { settings: {}, items: [] };
    }
    return JSON.parse(script.textContent);
  }

  forceLoadVisibleItems() {
    // Load any lazy items that are currently in viewport
    const lazyItems = this.querySelectorAll('.lazy-load-item');
    lazyItems.forEach(item => {
      if (this.isElementInViewport(item)) {
        this.loadCollectionItem(item);
      }
    });
  }

  isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }

  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver
      this.loadAllItems();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (entry.target.classList.contains('lazy-load-item')) {
            this.loadCollectionItem(entry.target);
          }
        }
      });
    }, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    });

    // Observe lazy load items
    this.querySelectorAll('.lazy-load-item').forEach(item => {
      this.observer.observe(item);
    });
  }

  loadAllItems() {
    // Fallback method to load all items without intersection observer
    this.querySelectorAll('.lazy-load-item').forEach(item => {
      this.loadCollectionItem(item);
    });
  }

  setupVideoAutoplay() {
    const videos = this.querySelectorAll('video.collection-video');
    videos.forEach(video => {
      video.addEventListener('loadedmetadata', () => {
        if (this.isElementInViewport(video)) {
          this.playVideo(video);
        }
      });
    });

    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          const video = entry.target;
          if (entry.isIntersecting) {
            this.playVideo(video);
          } else {
            video.pause();
          }
        });
      }, { threshold: 0.3 });

      videos.forEach(video => videoObserver.observe(video));
    }
  }

  setupImageLazyLoading() {
    if (!('IntersectionObserver' in window)) return;

    this.imageObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          this.loadImage(img);
        }
      });
    }, { rootMargin: '50px' });

    this.querySelectorAll('img.lazy').forEach(img => {
      this.imageObserver.observe(img);
    });
  }

  loadImage(img) {
    if (img.dataset.src) {
      img.onload = () => {
        img.classList.remove('lazy');
      };
      img.src = img.dataset.src;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      this.imageObserver?.unobserve(img);
    }
  }

  async playVideo(video) {
    try {
      video.muted = true;
      await video.play();
    } catch (error) {
      console.log('Video autoplay prevented:', error);
    }
  }

  async loadCollectionItem(item) {
    const blockIndex = parseInt(item.dataset.blockIndex);
    if (!blockIndex || this.loadedItems.has(blockIndex)) return;

    this.loadedItems.add(blockIndex);
    const itemData = this.collectionData.items.find(data => data.index === blockIndex);
    
    if (!itemData) {
      console.warn('Item data not found for index:', blockIndex);
      return;
    }

    try {
      const mediaElement = await this.createMediaElement(itemData);
      const skeleton = item.querySelector('.media-skeleton');
      
      if (skeleton && mediaElement) {
        // Replace skeleton with actual content
        skeleton.style.opacity = '0';
        
        setTimeout(() => {
          skeleton.replaceWith(mediaElement);
          item.classList.add('loaded');
          item.classList.remove('lazy-load-item');
          
          // Setup observers for new content
          const newImg = mediaElement.querySelector('img.lazy');
          if (newImg && this.imageObserver) {
            this.imageObserver.observe(newImg);
          }

          const newVideo = mediaElement.querySelector('video.collection-video');
          if (newVideo) {
            this.setupVideoAutoplay();
          }
        }, 300);
        
        this.observer?.unobserve(item);
      }
    } catch (error) {
      console.error('Failed to load collection item:', error);
      // Show fallback content
      this.showFallbackContent(item);
    }
  }

  showFallbackContent(item) {
    const skeleton = item.querySelector('.media-skeleton');
    if (skeleton) {
      skeleton.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Unable to load</div>';
    }
  }

  async createMediaElement(data) {
    const wrapper = document.createElement('div');
    wrapper.className = 'collection-media-container';

    if (data.media_type === 'video' && data.media_url) {
      // Create video element
      const video = document.createElement('video');
      video.className = `collection-video img-fluid ${this.collectionData.settings.item_img_border || ''}`;
      video.autoplay = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.style.objectFit = 'cover';
      video.style.width = '100%';
      video.style.height = '100%';
      
      // Set video source
      video.src = data.media_url;

      const source = document.createElement('source');
      source.src = data.media_url;
      source.type = 'video/mp4';
      video.appendChild(source);

      wrapper.appendChild(video);
    } else if (data.media_preview) {
      // Create image element
      const img = document.createElement('img');
      
      // Use a proper placeholder initially
      const size = this.collectionData.settings.item_img_size || 120;
      img.src = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${size} ${size}'%3E%3Crect width='100%25' height='100%25' fill='%23f8f9fa'/%3E%3C/svg%3E`;
      img.dataset.src = data.media_preview;
      img.className = `lazy img-fluid ${this.collectionData.settings.item_img_border || ''}`;
      img.alt = 'Collection image';
      img.loading = 'lazy';
      
      wrapper.appendChild(img);
    } else {
      // Fallback placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'placeholder-image';
      placeholder.style.cssText = `
        width: 100%; 
        height: 100%; 
        background: #f8f9fa; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        color: #6c757d;
        font-size: 14px;
      `;
      placeholder.textContent = 'No image';
      wrapper.appendChild(placeholder);
    }

    return wrapper;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('simple-collections', SimpleCollections);
  });
} else {
  customElements.define('simple-collections', SimpleCollections);
}

// Fallback for immediate loading if custom elements fail
setTimeout(() => {
  const collections = document.querySelectorAll('simple-collections');
  collections.forEach(collection => {
    if (!collection.loadedItems) {
      // Force load if not initialized
      const lazyItems = collection.querySelectorAll('.lazy-load-item');
      lazyItems.forEach(item => {
        const skeleton = item.querySelector('.media-skeleton');
        if (skeleton) {
          skeleton.style.display = 'none';
          const noscript = item.querySelector('noscript');
          if (noscript) {
            const content = noscript.innerHTML;
            const temp = document.createElement('div');
            temp.innerHTML = content;
            skeleton.parentNode.replaceChild(temp.firstElementChild, skeleton);
          }
        }
      });
    }
  });
}, 3000);
